import { ReviewChangeRequest, toEpochMs } from "../../types";

/**
 * Display order for a change-request list: newest first by submission time
 * (falling back to creation time), with the id as a final deterministic
 * tie-breaker.
 *
 * The order intentionally does **not** depend on `status`. Accepting,
 * rejecting or restoring a change request only flips its status, not its
 * submission/creation time, so the row keeps its exact position in the list
 * after the action instead of jumping into a different status tier. The new
 * state is conveyed by the status chip on the row, not by re-ordering. This
 * removes the disorienting "the row I just acted on moved away" behaviour.
 *
 * Pure and non-mutating (operates on a copy). The id tie-breaker keeps the
 * order fully deterministic across refetches even when two requests share a
 * timestamp.
 */
export const sortChangeRequestsForDisplay = (
  changeRequests: ReviewChangeRequest[],
): ReviewChangeRequest[] =>
  changeRequests.slice().sort((a, b) => {
    const byTime =
      toEpochMs(b.submittedAt || b.createdAt) -
      toEpochMs(a.submittedAt || a.createdAt);
    if (byTime !== 0) return byTime;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
